import { ArrayDataSource } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, Output } from '@angular/core';
// Models
import { Directory, PlanningItem, Region, RegionRef } from '@mocca-technologies/model';
// Services
import { DirectoryService } from '../../services/directory-manager.service';
import { LoadingController } from '@ionic/angular';
import { environment } from 'src/environments/environment';


@Component({
    selector: 'app-directory-tree',
    templateUrl: './directory-tree.component.html',
    styleUrls: ['./directory-tree.component.scss'],
})
export class DirectoryTreeComponent {

    /**
     * Input variables
     */
    @Input() items: PlanningItem[];
    @Input() multipleSelection: boolean; //Boolean used to control multiple item selection
    @Input() regionMapSelection: boolean = false; //Boolean used to control if the regionMap entered wants to show its items or the regionMap
    @Input() hideSearchBar: boolean = false; //Boolean used to control if the search bar is shown or not

    /**
     * Output variables
     */
    @Output() itemSelected = new EventEmitter<PlanningItem[]>();
    @Output() clearMap: EventEmitter<any> = new EventEmitter();

    /**
     * Variables
     */
    directoryTree: Directory[];
    dataSource: ArrayDataSource<Directory>;
    treeControl = new FlatTreeControl<Directory>(
        (node) => node.level,
        (node) => true
    );

    selectedItems: PlanningItem[] = [];
    selectedDirectories: Directory[] = [];

    searchTerm: string;

    // #region --> Getters ---------------------------------------------------
    /**
     *
     * @param node
     * @returns if the node has got items or not.
     */
    hasItems = (_: number, node: any) => node.items.length !== 0;

    /**
     *
     * @param node
     * @returns a boolean checking if the node should hide or no.
     */
    shouldHide(node: Directory): boolean {
        let parent = node.parentRef;
        while (parent) {
            if (parent.isExpanded === false) {
                return true;
            }
            parent = parent.parentRef;
        }
        return false;
    }

    /**
     * This method is used to know if an item should or not disable its checkbox depending on the multipleSelection and the selectedItems
     * @param item
     * @returns
     */
    shouldDisable(item: PlanningItem): boolean {
        if(!this.multipleSelection){
            if(this.selectedItems.length>0 && item!==this.selectedItems[0]){
                return true;
            } else{
                return false;
            }
        } else{
            return false;
        }
    }

    /**
     * This method is used to know if there are items in our directory tree or not.
     * @returns if the planningItem array has items.
     */
    planningItemArrayHasItems(){
        return this.directoryTree?.length > 0;
    }

    // #endregion --> Getters ------------------------------------------------

    constructor(private directoryService: DirectoryService) {
    }

    // #region --> Data Loading ---------------------------------------------------

    /**
     * Method used from parent in order to load the items and convert them into a tree
     */
    loadItems(items?: PlanningItem[]) {
        this.selectedItems = [];
        this.directoryTree = [];
        this.dataSource = new ArrayDataSource([]);


        if(items) {
            this.selectedItems = [...items];
            items?.forEach((item) => {
                this.items?.find((planningItem) => {
                    if (planningItem.kind === 'trajectory' && (planningItem?.uniqueId === item?.uniqueId)) {
                        planningItem.isChecked = true;
                    }
                    if (planningItem.kind === 'regionMap' && (planningItem?.name === item?.name)){
                        planningItem.isChecked = true;
                    }
                    if (item.kind === 'region' && planningItem.kind === 'regionMap') {
                        if(planningItem.regions){
                            planningItem.regions.forEach((regionRef: RegionRef) => {
                                const region = regionRef.region as Region;
                                if(region?.name === item?.name){
                                    region.isChecked = true;
                                }
                            })
                        }
                    }
                });
            });
        }

        this.directoryTree = this.directoryService.buildTree(this.items, this.regionMapSelection);
        this.dataSource = new ArrayDataSource(this.directoryTree);

        this.directoryTree.forEach((directory) => {
            directory.items.forEach((item) => {
                if(item.isChecked){
                  directory.isExpanded = true;
                  if(directory.parentRef != null) {
                    directory.parentRef.isExpanded = true;
                  }
                }
            });
        });
    }

    // #endregion --> Data Loading ---------------------------------------------------

    // #region --> Methods ---------------------------------------------------
    /**
     * This method changes the expanded state of a node.
     * @param node
     * @returns the expanded state for the selected node.
     */
    expandNode = (node: any) => (node.isExpanded = !node.isExpanded);

    /**
     * This method changes the state of an item, and reorders all the badge counts.
     * @param ev
     * @param item
     */
    changeItemState(item: PlanningItem) {

        // Manage the selectedItems list
        if(!this.multipleSelection){

            if(this.selectedItems.length > 0) {
                this.directoryTree.forEach((directory) => {
                    directory?.items.forEach((directoryItem) => directoryItem.isChecked = false)
                })
            }

            if(this.selectedItems[0] == item){
                this.selectedItems = [];
                this.clearMap.emit();
                this.itemSelected.emit([]);
            } else {
                this.selectedItems = [];
                this.clearMap.emit();

                item.isChecked = true;

                this.selectedItems = [item];

                this.itemSelected.emit([...this.selectedItems]);
            }
        } else{
            let itemIndex = this.selectedItems.findIndex(i => i.uniqueId === item.uniqueId);
            if(itemIndex===-1){
                this.selectedItems = [...this.selectedItems, item];
            } else{
                this.selectedItems = this.selectedItems.filter(i => i.uniqueId !== item.uniqueId);
            }
            this.itemSelected.emit([...this.selectedItems]);
        }
    }

    /**
     *
     * @param ms
     * @returns
     */
    delay(ms: number) {
        return new Promise( resolve => setTimeout(resolve, ms) );
    }

    /**
     * This method is used to filter the tree depending on a search term
     */
    addSearchTerm() {
        if(this.searchTerm !== '' && this.searchTerm !== null && this.searchTerm !== undefined) {
            this.selectedDirectories = [];
            let searchTermList: string[] = this.searchTerm.split(", ").map(term => term.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''));

            this.directoryTree.forEach((dir) => {
                if (this.directoryMatchesAllTerms(dir, searchTermList)) {
                    this.includeParents(dir);
                    this.includeChildren(dir);
                }
            });

            this.selectedDirectories = this.directoryService.orderDirectoryTree(this.selectedDirectories);
            this.dataSource = new ArrayDataSource(this.selectedDirectories);
        } else {
            this.directoryTree.forEach((dir) => dir.isExpanded = false);
            this.dataSource = new ArrayDataSource(this.directoryTree);
        }
    }

    directoryMatchesAllTerms(directory: Directory, terms: string[]): boolean {
        return terms.every(term =>
            directory.name.toLowerCase().includes(term) || this.itemsIncludeSearchTerm(directory, term)
        );
    }

    /**
     * This method is used to add a directory to the selected directories list
     * @param directory
     */
    addDirectory(directory: Directory) {
        if(!this.selectedDirectories.includes(directory)) {
            this.selectedDirectories.push(directory);
        }
    }

    /**
     * This method is used to add parent directories to the list of selected directories
     * @param directory
     */
    includeParents(directory: Directory) {
        if (directory) {
            let newDirectory = directory;
            newDirectory.isExpanded = true;
            this.addDirectory(newDirectory);
            this.includeParents(newDirectory.parentRef);

        }
    }

    /**
     * This method is used to add children directories to the list of selected directories
     * @param directory
     */
    includeChildren(directory: Directory) {
        let newDirectory = directory;
        newDirectory.isExpanded = true;
        this.addDirectory(newDirectory);

        this.directoryTree.forEach((dir) => {
            if(dir.parentRef === newDirectory) {
                this.includeChildren(dir);
            }
        });
    }

    /**
     * This method is used to search if items in directories have got the search term
     * @param dir
     * @param searchTerm
     * @returns
     */
    itemsIncludeSearchTerm(dir: Directory, searchTerm: string): boolean {

        if (dir.items.length > 0) {

            for (const item of dir.items) {
                let name: string | null = null;
                let version: string | null = null;

                switch (item.kind){
                    case 'apOperation':
                        name = item.name;
                        version = item.version;
                        break;
                    case 'region':
                        name = item.name;
                        version = null;
                        break;
                    case 'regionMap':
                        name = item.name;
                        version = null;
                        break;
                    case 'trajectory':
                        name = item.name;
                        version = item.version;
                        break;
                    default:
                        name = null;
                        version = null;
                        break;
                }

                if (name && name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(searchTerm)){
                    return true;
                }

                if (version && version.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(searchTerm)) {
                    return true;
                }
            }
        }

        return false;
    }

    // #endregion --> Methods ---------------------------------------------------

    }
